import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { RfpStrategicCarriersResponse } from "../types/rfpApi";

interface UseRfpStrategicCarriersOptions<TSelected> {
  rfpId: string;
  enabled?: boolean;
  select?: (data: RfpStrategicCarriersResponse) => TSelected;
}

export const getRfpStrategicCarriersQueryKey = (rfpId: string): string[] => [
  "rfps",
  rfpId,
  "carriers",
];

export const getRfpStrategicCarriersEndpoint = (rfpId: string): string =>
  `brokerportal/v1/rfp/${rfpId}/carriers/strategic`;

// useRfpStrategicCarriers with selector overload
export function useRfpStrategicCarriers<TSelected>(options: {
  rfpId: string;
  enabled?: boolean;
  select: (data: RfpStrategicCarriersResponse) => TSelected;
}): UseQueryResult<TSelected, Error>;

// useRfpStrategicCarriers with default select for all RFP carriers response data
export function useRfpStrategicCarriers(options: {
  rfpId: string;
  enabled?: boolean;
}): UseQueryResult<RfpStrategicCarriersResponse, Error>;

// useRfpStrategicCarriers fetches carriers for an RFP project
export function useRfpStrategicCarriers<
  TSelected = RfpStrategicCarriersResponse,
>({
  rfpId,
  enabled = true,
  select,
}: UseRfpStrategicCarriersOptions<TSelected>): UseQueryResult<
  TSelected,
  Error
> {
  const axios = useApi();
  return useQuery({
    queryKey: getRfpStrategicCarriersQueryKey(rfpId),
    enabled,
    select,
    queryFn: () =>
      axios
        .get<RfpStrategicCarriersResponse>(
          getRfpStrategicCarriersEndpoint(rfpId),
        )
        .then((res) => res.data),
  });
}
